import React from "react";
import { Box, Sheet } from "@mui/joy";
import DarkTypography from "../../../common/DarkTypography";
import type { MailPlaceholderDefinition, MailTemplate } from "api/mailTemplates/MailTemplate.typed";
import {
  colorTokens,
  mailEditorBodyWorkspaceSx,
  mailEditorPreviewSheetSx,
} from "../../../../styles/globalStyles";
import MailEditorAddBlockMenu from "./MailEditorAddBlockMenu";
import type { MailBodyBlock } from "../mailBodyBlocks";
import { createEmptyMailBodyBlock } from "../mailBodyBlocks";
import MailBodyBlockEditor from "../MailBodyBlockEditor";
import type { MailBodyBlockEditorHandle } from "../MailBodyBlockEditor";
import type { MailPlaceholderInsertTarget } from "../mailPlaceholderContext";

const bodySplitRowSx = {
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: { xs: "column", md: "row" },
  gap: { xs: 1, md: 1.25 },
  overflow: "hidden",
} as const;

const editColumnSx = {
  flex: { xs: "1 1 auto", md: 1 },
  minWidth: 0,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
} as const;

const sectionHeaderRowSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  flexShrink: 0,
  mb: 0.5,
  flexWrap: "wrap",
  minWidth: 0,
} as const;

const editScrollAreaSx = {
  flex: 1,
  minHeight: 0,
  overflow: "auto",
  overflowX: "hidden",
  scrollbarGutter: "stable",
  pr: { xs: 0, sm: 0.25 },
} as const;

const previewColumnSx = {
  flex: { xs: "none", md: 1 },
  minWidth: 0,
  minHeight: { xs: 260, md: 0 },
  height: { xs: "min(42vh, 360px)", md: "auto" },
  display: "flex",
  flexDirection: "column",
} as const;

const previewIframeHostSx = {
  flex: 1,
  minHeight: 0,
  position: "relative",
  borderRadius: 6,
  overflow: "hidden",
  bgcolor: colorTokens.bg.sheetLight,
} as const;

type MailEditorBodyWorkspaceProps = {
  template: MailTemplate | undefined;
  blocks: MailBodyBlock[];
  onBlocksChange: (b: MailBodyBlock[]) => void;
  canEdit: boolean;
  blockEditorRef: React.RefObject<MailBodyBlockEditorHandle>;
  previewFullHtml: string;
  previewLoading: boolean;
  booleanConditionPlaceholders: MailPlaceholderDefinition[];
  placeholderCatalog: MailPlaceholderDefinition[];
  onPlaceholderTargetChange: (target: MailPlaceholderInsertTarget) => void;
};

export default function MailEditorBodyWorkspace(props: MailEditorBodyWorkspaceProps) {
  const {
    template,
    blocks,
    onBlocksChange,
    canEdit,
    blockEditorRef,
    previewFullHtml,
    previewLoading,
    booleanConditionPlaceholders,
    placeholderCatalog,
    onPlaceholderTargetChange,
  } = props;

  const appendBlock = React.useCallback(
    (type: MailBodyBlock["type"]) => {
      const next = createEmptyMailBodyBlock(type);

      if (next) {
        onBlocksChange([...blocks, next]);
      }
    },
    [blocks, onBlocksChange],
  );

  return (
    <Box sx={mailEditorBodyWorkspaceSx}>
      <Box sx={bodySplitRowSx}>
        <Box sx={editColumnSx}>
          <Box sx={sectionHeaderRowSx}>
            <DarkTypography level="title-sm" colorVariant="legend">
              Édition
            </DarkTypography>
            {canEdit && template && <MailEditorAddBlockMenu onPickType={appendBlock} />}
          </Box>
          <Box sx={editScrollAreaSx} onFocusCapture={() => onPlaceholderTargetChange("body")}>
            {template && (
              <MailBodyBlockEditor
                ref={blockEditorRef}
                blocks={blocks}
                onChange={onBlocksChange}
                canEdit={canEdit}
                booleanConditionPlaceholders={booleanConditionPlaceholders}
                placeholderLabelCatalog={placeholderCatalog}
              />
            )}
          </Box>
        </Box>
        <Box sx={previewColumnSx}>
          <DarkTypography level="title-sm" colorVariant="legend" sx={{ mb: 0.5, flexShrink: 0 }}>
            Aperçu
          </DarkTypography>
          <Sheet variant="outlined" sx={{ ...mailEditorPreviewSheetSx, flex: 1 }}>
            {!template ? (
              <DarkTypography level="body-sm" colorVariant="primary" sx={{ p: 1 }}>
                …
              </DarkTypography>
            ) : previewLoading ? (
              <DarkTypography level="body-sm" colorVariant="primary" sx={{ p: 1 }}>
                …
              </DarkTypography>
            ) : (
              <Box sx={previewIframeHostSx}>
                <iframe
                  title="mail-preview"
                  srcDoc={previewFullHtml}
                  style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    border: 0,
                    background: colorTokens.bg.sheetLight,
                  }}
                  sandbox="allow-popups allow-popups-to-escape-sandbox"
                />
              </Box>
            )}
          </Sheet>
        </Box>
      </Box>
    </Box>
  );
}
